import { apiRequest, queryString } from "./http";
import type {
  ArchiveUserPayload,
  AssignmentOption,
  AssignmentOptionsParams,
  CreateUserPayload,
  CreatedUserResult,
  CursorPage,
  InvitationResult,
  UpdateUserPayload,
  UserDependencySummary,
  UserDirectoryItem,
  UserDirectoryParams,
  UserListParams,
  UserRecord,
} from "./generated/openapi";

export type {
  ArchiveUserPayload,
  AssignmentOption,
  AssignmentOptionsParams,
  CreateUserPayload,
  CreatedUserResult,
  CursorPage,
  InvitationResult,
  UpdateUserPayload,
  UserDependencySummary,
  UserDirectoryItem,
  UserDirectoryParams,
  UserDirectoryStatus,
  UserListParams,
  UserRecord,
  UserRole,
} from "./generated/openapi";

export const userApi = {
  list: (params: UserListParams = {}) =>
    apiRequest<UserRecord[]>(`/api/users${queryString(params)}`),

  directory: (params: UserDirectoryParams = {}, signal?: AbortSignal) =>
    apiRequest<CursorPage<UserDirectoryItem>>(
      `/api/users/directory${queryString(params)}`,
      { signal },
    ),

  assignmentOptions: (params: AssignmentOptionsParams, signal?: AbortSignal) =>
    apiRequest<CursorPage<AssignmentOption>>(
      `/api/users/assignment-options${queryString(params)}`,
      { signal },
    ),

  create: (payload: CreateUserPayload) =>
    apiRequest<CreatedUserResult>("/api/users", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  get: (id: string, signal?: AbortSignal) =>
    apiRequest<UserRecord>(`/api/users/${id}`, { signal }),

  update: (id: string, payload: UpdateUserPayload) =>
    apiRequest<UserRecord>(`/api/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  archive: (id: string, payload: ArchiveUserPayload) =>
    apiRequest<UserRecord>(`/api/users/${id}/archive`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  restore: (id: string, expectedVersion: number) =>
    apiRequest<UserRecord>(`/api/users/${id}/restore`, {
      method: "POST",
      body: JSON.stringify({ expected_version: expectedVersion }),
    }),

  dependencies: (id: string, signal?: AbortSignal) =>
    apiRequest<UserDependencySummary>(`/api/users/${id}/dependencies`, {
      signal,
    }),

  invite: (id: string) =>
    apiRequest<InvitationResult>(`/api/users/${id}/invitation`, {
      method: "POST",
    }),

  permanentlyDelete: (id: string, expectedVersion: number) =>
    apiRequest<UserRecord>(
      `/api/users/${id}?confirm=true&expected_version=${expectedVersion}`,
      { method: "DELETE" },
    ),

  resendInvitation: (id: string) =>
    apiRequest<InvitationResult>(`/api/users/${id}/invitation`, {
      method: "POST",
    }),
};
